/**
 * Mock messages service.
 * Mirrors the real messages.service.js API with in-memory data.
 */
import {
  MOCK_CONVERSATIONS,
  MOCK_MESSAGES,
  makeConversationId,
} from '../../data/mock/mock-messages.data';
import { getMockUserById } from '../../data/mock/mock-users.data';
import { mockDelay, mockLog, mockId } from './mock-delay';

// Deep clone to allow mutation
let conversations = JSON.parse(JSON.stringify(MOCK_CONVERSATIONS));
let messages = JSON.parse(JSON.stringify(MOCK_MESSAGES));

// Store listener callbacks for simulating real-time updates
let conversationListeners = [];
let messageListeners = {};
let typingListeners = {};
let unreadListeners = [];

/**
 * Notify all conversation list listeners.
 */
const notifyConversationListeners = () => {
  conversationListeners.forEach(({ userId, callback }) => {
    const userConvs = conversations
      .filter((c) => c.participants.includes(userId))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    callback(userConvs);
  });
};

/**
 * Notify message listeners for a specific conversation.
 */
const notifyMessageListeners = (conversationId) => {
  const listeners = messageListeners[conversationId] || [];
  const msgs = (messages[conversationId] || []).sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );
  listeners.forEach((callback) => callback(msgs));
};

/**
 * Notify typing listeners for a specific conversation.
 */
const notifyTypingListeners = (conversationId) => {
  const listeners = typingListeners[conversationId] || [];
  const conv = conversations.find((c) => c.id === conversationId);
  if (conv) {
    listeners.forEach((callback) => callback(conv.typing || {}));
  }
};

/**
 * Notify all unread count listeners.
 */
const notifyUnreadListeners = () => {
  unreadListeners.forEach(({ userId, callback }) => {
    const total = conversations.reduce((sum, conv) => {
      if (conv.participants.includes(userId)) {
        return sum + (conv.unreadCount[userId] || 0);
      }
      return sum;
    }, 0);
    callback(total);
  });
};

export const getOrCreateConversation = async (userId1, userId2, user1Details, user2Details) => {
  mockLog('MessagesService', 'getOrCreateConversation', { userId1, userId2 });
  await mockDelay(100, 300);

  const conversationId = makeConversationId(userId1, userId2);
  const existing = conversations.find((c) => c.id === conversationId);

  if (existing) {
    return conversationId;
  }

  // Look up user details from mock data if not provided
  const u1 = user1Details || getMockUserById(userId1) || {};
  const u2 = user2Details || getMockUserById(userId2) || {};

  const newConv = {
    id: conversationId,
    participants: [userId1, userId2],
    participantDetails: {
      [userId1]: {
        displayName: u1.displayName || '',
        username: u1.username || '',
        avatarUrl: u1.avatarUrl || '',
      },
      [userId2]: {
        displayName: u2.displayName || '',
        username: u2.username || '',
        avatarUrl: u2.avatarUrl || '',
      },
    },
    lastMessage: null,
    unreadCount: { [userId1]: 0, [userId2]: 0 },
    typing: { [userId1]: false, [userId2]: false },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  conversations = [newConv, ...conversations];
  messages[conversationId] = [
    {
      id: `msg_system_${mockId()}`,
      senderId: 'system',
      text: 'Conversation started',
      imageUrl: null,
      readBy: [userId1, userId2],
      createdAt: new Date().toISOString(),
      type: 'system',
    },
  ];

  notifyConversationListeners();
  notifyMessageListeners(conversationId);

  return conversationId;
};

export const getConversations = async (userId) => {
  mockLog('MessagesService', 'getConversations', userId);
  await mockDelay();

  return conversations
    .filter((c) => c.participants.includes(userId))
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
};

export const onConversationsChange = (userId, callback) => {
  mockLog('MessagesService', 'onConversationsChange', userId);

  const listener = { userId, callback };
  conversationListeners.push(listener);

  // Initial callback
  setTimeout(() => {
    const userConvs = conversations
      .filter((c) => c.participants.includes(userId))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    callback(userConvs);
  }, 100);

  return () => {
    conversationListeners = conversationListeners.filter((l) => l !== listener);
  };
};

export const sendMessage = async (conversationId, senderId, text, imageUrl = null, replyTo = null) => {
  mockLog('MessagesService', 'sendMessage', { conversationId, senderId, text: text?.substring(0, 30) });
  await mockDelay(50, 200);

  const conv = conversations.find((c) => c.id === conversationId);
  if (!conv) throw new Error('Conversation not found.');

  const otherUserId = conv.participants.find((p) => p !== senderId);
  const messageType = imageUrl ? 'image' : 'text';
  const messageText = imageUrl && !text ? 'Sent an image' : text;

  const newMsg = {
    id: `msg_${mockId()}`,
    senderId,
    text: messageText,
    imageUrl: imageUrl || null,
    readBy: [senderId],
    reactions: {},
    replyTo: replyTo ? {
      messageId: replyTo.messageId || '',
      text: replyTo.text || '',
      senderName: replyTo.senderName || '',
    } : null,
    createdAt: new Date().toISOString(),
    type: messageType,
  };

  if (!messages[conversationId]) {
    messages[conversationId] = [];
  }
  messages[conversationId].push(newMsg);

  // Update conversation
  conv.lastMessage = {
    text: messageText,
    senderId,
    timestamp: new Date().toISOString(),
  };
  conv.unreadCount[otherUserId] = (conv.unreadCount[otherUserId] || 0) + 1;
  conv.typing[senderId] = false;
  conv.updatedAt = new Date().toISOString();

  notifyConversationListeners();
  notifyMessageListeners(conversationId);
  notifyUnreadListeners();
};

export const getMessages = async (conversationId, lastDoc = null, pageLimit = 50) => {
  mockLog('MessagesService', 'getMessages', { conversationId, lastDoc, pageLimit });
  await mockDelay();

  const allMsgs = (messages[conversationId] || []).sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  let startIndex = 0;
  if (lastDoc) {
    const idx = allMsgs.findIndex((m) => m.id === lastDoc);
    if (idx !== -1) startIndex = idx + 1;
  }

  const page = allMsgs.slice(startIndex, startIndex + pageLimit);
  const newLastDoc = page.length > 0 ? page[page.length - 1].id : null;
  const hasMore = startIndex + pageLimit < allMsgs.length;

  return { messages: page, lastDoc: newLastDoc, hasMore };
};

export const onMessagesChange = (conversationId, callback) => {
  mockLog('MessagesService', 'onMessagesChange', conversationId);

  if (!messageListeners[conversationId]) {
    messageListeners[conversationId] = [];
  }
  messageListeners[conversationId].push(callback);

  // Initial callback
  setTimeout(() => {
    const msgs = (messages[conversationId] || []).sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
    callback(msgs);
  }, 100);

  return () => {
    messageListeners[conversationId] = (messageListeners[conversationId] || []).filter(
      (cb) => cb !== callback
    );
  };
};

export const markConversationRead = async (conversationId, userId) => {
  mockLog('MessagesService', 'markConversationRead', { conversationId, userId });
  await mockDelay(50, 150);

  const conv = conversations.find((c) => c.id === conversationId);
  if (conv) {
    conv.unreadCount[userId] = 0;
  }

  // Mark messages as read
  if (messages[conversationId]) {
    messages[conversationId] = messages[conversationId].map((msg) => ({
      ...msg,
      readBy: msg.readBy.includes(userId) ? msg.readBy : [...msg.readBy, userId],
    }));
  }

  notifyConversationListeners();
  notifyMessageListeners(conversationId);
  notifyUnreadListeners();
};

export const setTyping = async (conversationId, userId, isTyping) => {
  mockLog('MessagesService', 'setTyping', { conversationId, userId, isTyping });

  const conv = conversations.find((c) => c.id === conversationId);
  if (conv) {
    conv.typing[userId] = isTyping;
    notifyTypingListeners(conversationId);
  }
};

export const onTypingChange = (conversationId, callback) => {
  mockLog('MessagesService', 'onTypingChange', conversationId);

  if (!typingListeners[conversationId]) {
    typingListeners[conversationId] = [];
  }
  typingListeners[conversationId].push(callback);

  // Initial callback
  const conv = conversations.find((c) => c.id === conversationId);
  setTimeout(() => callback(conv?.typing || {}), 100);

  return () => {
    typingListeners[conversationId] = (typingListeners[conversationId] || []).filter(
      (cb) => cb !== callback
    );
  };
};

export const getTotalUnreadCount = async (userId) => {
  mockLog('MessagesService', 'getTotalUnreadCount', userId);
  await mockDelay(50, 150);

  return conversations.reduce((total, conv) => {
    if (conv.participants.includes(userId)) {
      return total + (conv.unreadCount[userId] || 0);
    }
    return total;
  }, 0);
};

export const onTotalUnreadChange = (userId, callback) => {
  mockLog('MessagesService', 'onTotalUnreadChange', userId);

  const listener = { userId, callback };
  unreadListeners.push(listener);

  // Initial callback
  setTimeout(() => {
    const total = conversations.reduce((sum, conv) => {
      if (conv.participants.includes(userId)) {
        return sum + (conv.unreadCount[userId] || 0);
      }
      return sum;
    }, 0);
    callback(total);
  }, 100);

  return () => {
    unreadListeners = unreadListeners.filter((l) => l !== listener);
  };
};

export const deleteConversation = async (conversationId, userId) => {
  mockLog('MessagesService', 'deleteConversation', { conversationId, userId });
  await mockDelay();

  // For mock, just remove from list
  conversations = conversations.filter((c) => c.id !== conversationId);
  notifyConversationListeners();
  notifyUnreadListeners();
};

export const addReaction = async (conversationId, messageId, emoji, userId) => {
  mockLog('MessagesService', 'addReaction', { conversationId, messageId, emoji, userId });
  await mockDelay(50, 150);

  const convMessages = messages[conversationId];
  if (!convMessages) return;

  const msg = convMessages.find((m) => m.id === messageId);
  if (!msg) return;

  if (!msg.reactions) msg.reactions = {};
  if (!msg.reactions[emoji]) msg.reactions[emoji] = [];

  if (!msg.reactions[emoji].includes(userId)) {
    msg.reactions[emoji].push(userId);
  }

  notifyMessageListeners(conversationId);
};

export const removeReaction = async (conversationId, messageId, emoji, userId) => {
  mockLog('MessagesService', 'removeReaction', { conversationId, messageId, emoji, userId });
  await mockDelay(50, 150);

  const convMessages = messages[conversationId];
  if (!convMessages) return;

  const msg = convMessages.find((m) => m.id === messageId);
  if (!msg) return;

  if (!msg.reactions || !msg.reactions[emoji]) return;

  msg.reactions[emoji] = msg.reactions[emoji].filter((id) => id !== userId);

  // Clean up empty arrays
  if (msg.reactions[emoji].length === 0) {
    delete msg.reactions[emoji];
  }

  notifyMessageListeners(conversationId);
};

export const uploadChatImage = async (file, conversationId, onProgress) => {
  mockLog('MessagesService', 'uploadChatImage', conversationId);
  if (onProgress) onProgress(50);
  await mockDelay(300, 600);
  if (onProgress) onProgress(100);
  return 'https://placehold.co/600x400/e8e8e8/666?text=Chat+Image';
};
